import { ACTIVITY_DISTRICT_LIMIT, ACTIVITY_PRODUCT_LIMIT, CHANNEL_NAMES, type ActivityChannelRow, type ActivityCityRow, type ActivityPlaceRow, type ActivityProductRow, type ActivitySellerRow } from '@wms/domain';
import { Fragment, useState } from 'react';
import { fmt } from '../../format';
import { daysText, moneyListText, percentText } from '../../performance';
import { Empty, SellerChip } from '../../ui';
import { Icon } from '../../ui/icons';
import { useSort } from '../../ui/sort';

/** "%12,5" with a bar of that length */
function Share({ rate }: { rate: number }) {
  return (
    <span className="share">
      <span className="share-bar" aria-hidden="true">
        <i style={{ width: `${Math.min(100, rate * 100)}%` }} />
      </span>
      {percentText(rate)}
    </span>
  );
}

/** The warehouse's sellers side by side; a row narrows the page to that seller. */
export function SellersTable({ rows, onSeller }: { rows: readonly ActivitySellerRow[]; onSeller: (tenantId: string) => void }) {
  const sort = useSort<ActivitySellerRow>('perf.sellers', {
    seller: { label: 'Satıcı', value: (r) => r.tenantCode },
    received: { label: 'Alınan', value: (r) => r.received, desc: true },
    units: { label: 'Adet', value: (r) => r.units, desc: true },
    shipped: { label: 'Kargoya verilen', value: (r) => r.shipped, desc: true },
    onTime: { label: 'Zamanında', value: (r) => r.onTimeRate, desc: true },
    returns: { label: 'İade', value: (r) => r.returnedOrders, desc: true },
    // the lira amount orders the column; other currencies follow it
    fees: { label: 'Ücret', value: (r) => Number(r.fees.find((m) => m.currency === 'TRY')?.amount ?? 0), desc: true },
  });
  return (
    <section className="card">
      <div className="card-h">
        <Icon name="building" />
        <h2>Satıcılar</h2>
        <span className="sub">Zamanında: son kargo zamanı bu dönemde dolan siparişlerden zamanında verilenler. İade: bu dönemde iadesi gelen sipariş. Satıra dokunun: sayfa o satıcıya daralır.</span>
        <span className="spacer" />
        {sort.picker}
      </div>
      {rows.length === 0 ? (
        <Empty title="Bu dönemde satıcı hareketi yok" />
      ) : (
        <div className="table-wrap">
          <table className="t">
            <thead>
              <tr>
                {sort.th('seller')}
                {sort.th('received', 'num')}
                {sort.th('units', 'num hide-sm')}
                {sort.th('shipped', 'num hide-sm')}
                {sort.th('onTime', 'num')}
                {sort.th('returns', 'num hide-sm')}
                {sort.th('fees', 'num')}
              </tr>
            </thead>
            <tbody>
              {sort.apply(rows).map((r) => (
                <tr key={r.tenantId} className="clickable" onClick={() => onSeller(r.tenantId)} title={`${r.tenantName}: yalnız bu satıcının rakamlarını göster`}>
                  <td>
                    <SellerChip code={r.tenantCode} /> <span className="muted hide-sm">{r.tenantName}</span>
                  </td>
                  <td className="num">{fmt.n(r.received)}</td>
                  <td className="num hide-sm">{fmt.n(r.units)}</td>
                  <td className="num hide-sm">{fmt.n(r.shipped)}</td>
                  <td className="num" title={`${fmt.n(r.due)} siparişin son kargo zamanı bu dönemde doldu`}>
                    {percentText(r.onTimeRate)}
                  </td>
                  <td className="num hide-sm">{fmt.n(r.returnedOrders)}</td>
                  <td className="num">{moneyListText(r.fees)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export function ProductsTable({ rows, count, showSeller }: { rows: readonly ActivityProductRow[]; count: number; showSeller: boolean }) {
  const sort = useSort<ActivityProductRow>('perf.products', {
    ...(showSeller ? { seller: { label: 'Satıcı', value: (r: ActivityProductRow) => r.tenantCode } } : {}),
    product: { label: 'Ürün', value: (r) => r.sku },
    orders: { label: 'Sipariş', value: (r) => r.orders, desc: true },
    units: { label: 'Adet', value: (r) => r.units, desc: true },
    returned: { label: 'İade adedi', value: (r) => r.returnedUnits, desc: true },
  });
  return (
    <section className="card">
      <div className="card-h">
        <Icon name="tag" />
        <h2>Ürünler</h2>
        <span className="sub">
          Bu dönemde kargoya verilen siparişlerdeki ürünler, en çok gönderilen önce.
          {count > rows.length ? ` İlk ${fmt.n(ACTIVITY_PRODUCT_LIMIT)} ürün gösteriliyor (toplam ${fmt.n(count)}).` : ''} İade adedi: bu dönemde iade kabulde alınan.
        </span>
        <span className="spacer" />
        {sort.picker}
      </div>
      {rows.length === 0 ? (
        <Empty title="Bu dönemde kargoya verilen ürün yok" />
      ) : (
        <div className="table-wrap">
          <table className="t">
            <thead>
              <tr>
                {showSeller && sort.th('seller')}
                {sort.th('product')}
                {sort.th('orders', 'num hide-sm')}
                {sort.th('units', 'num')}
                {sort.th('returned', 'num')}
              </tr>
            </thead>
            <tbody>
              {sort.apply(rows).map((r) => (
                <tr key={r.productId}>
                  {showSeller && (
                    <td>
                      <SellerChip code={r.tenantCode} />
                    </td>
                  )}
                  <td>
                    <b className="mono">{r.sku}</b>
                    <div className="muted" style={{ fontSize: 13 }}>
                      {r.name}
                    </div>
                  </td>
                  <td className="num hide-sm">{fmt.n(r.orders)}</td>
                  <td className="num">{fmt.n(r.units)}</td>
                  <td className="num">{r.returnedUnits ? <span className="chip warn">{fmt.n(r.returnedUnits)}</span> : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export function ChannelsTable({ rows }: { rows: readonly ActivityChannelRow[] }) {
  const sort = useSort<ActivityChannelRow>('perf.channels', {
    channel: { label: 'Kanal', value: (r) => CHANNEL_NAMES[r.channel] ?? r.channel },
    received: { label: 'Sipariş', value: (r) => r.received, desc: true },
    cancelled: { label: 'İptal', value: (r) => r.cancelled, desc: true },
    units: { label: 'Adet', value: (r) => r.units, desc: true },
    share: { label: 'Pay', value: (r) => r.share, desc: true },
  });
  return (
    <section className="card">
      <div className="card-h">
        <Icon name="plug" />
        <h2>Kanallar</h2>
        <span className="sub">Bu dönemde alınan siparişler, geldikleri pazaryerine göre. Adet: iptal edilmeyenlerin.</span>
        <span className="spacer" />
        {sort.picker}
      </div>
      {rows.length === 0 ? (
        <Empty title="Bu dönemde sipariş alınmadı" />
      ) : (
        <div className="table-wrap">
          <table className="t">
            <thead>
              <tr>
                {sort.th('channel')}
                {sort.th('received', 'num')}
                {sort.th('cancelled', 'num hide-sm')}
                {sort.th('units', 'num hide-sm')}
                {sort.th('share', 'num')}
              </tr>
            </thead>
            <tbody>
              {sort.apply(rows).map((r) => (
                <tr key={r.channel}>
                  <td>{CHANNEL_NAMES[r.channel] ?? r.channel}</td>
                  <td className="num">{fmt.n(r.received)}</td>
                  <td className="num hide-sm">{fmt.n(r.cancelled)}</td>
                  <td className="num hide-sm">{fmt.n(r.units)}</td>
                  <td className="num">
                    <Share rate={r.share} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

const placeColumns = (label: string) => ({
  name: { label, value: (r: ActivityPlaceRow) => r.name },
  orders: { label: 'Sipariş', value: (r: ActivityPlaceRow) => r.orders, desc: true },
  delivery: { label: 'Teslim süresi', value: (r: ActivityPlaceRow) => r.deliveryMedianHours },
});

/** Cities by orders shipped there; a city opens its districts. */
export function RegionsTable({ cities }: { cities: readonly ActivityCityRow[] }) {
  const sort = useSort<ActivityCityRow>('perf.cities', placeColumns('İl'));
  const [open, setOpen] = useState<string | null>(null);
  return (
    <section className="card">
      <div className="card-h">
        <Icon name="map" />
        <h2>Bölgeler</h2>
        <span className="sub">
          Bu dönemde kargoya verilen siparişler, teslimat adresinin il ve ilçesine göre (adresten yalnız bunlar saklanır). Teslim süresi: teslim edilenlerde kargodan alıcıya, ortanca. İlçeler için ile dokunun.
        </span>
        <span className="spacer" />
        {sort.picker}
      </div>
      {cities.length === 0 ? (
        <Empty title="Bu dönemde kargoya verilen sipariş yok" />
      ) : (
        <div className="table-wrap">
          <table className="t">
            <thead>
              <tr>
                {sort.th('name')}
                {sort.th('orders', 'num')}
                <th className="num">Pay</th>
                {sort.th('delivery', 'num')}
                <th />
              </tr>
            </thead>
            <tbody>
              {sort.apply(cities).map((c) => {
                const expanded = open === c.key;
                return (
                  <Fragment key={c.key}>
                    <tr className="clickable" onClick={() => setOpen(expanded ? null : c.key)} aria-expanded={expanded}>
                      <td>
                        <b>{c.name}</b>
                      </td>
                      <td className="num">{fmt.n(c.orders)}</td>
                      <td className="num">
                        <Share rate={c.share} />
                      </td>
                      <td className="num" title={`${fmt.n(c.delivered)} sipariş teslim edildi`}>
                        {daysText(c.deliveryMedianHours)}
                      </td>
                      <td>
                        <Icon name={expanded ? 'x' : 'list'} size={16} />
                      </td>
                    </tr>
                    {expanded && (
                      <tr className="sub-row">
                        <td colSpan={5}>
                          <Districts city={c} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function Districts({ city }: { city: ActivityCityRow }) {
  const sort = useSort<ActivityPlaceRow>('perf.districts', placeColumns('İlçe'));
  return (
    <>
      {city.districtCount > city.districts.length && (
        <p className="muted" style={{ fontSize: 13, marginBottom: 6 }}>
          En çok sipariş giden ilk {fmt.n(ACTIVITY_DISTRICT_LIMIT)} ilçe (toplam {fmt.n(city.districtCount)}).
        </p>
      )}
      <table className="t">
        <thead>
          <tr>
            {sort.th('name')}
            {sort.th('orders', 'num')}
            <th className="num" title={`${city.name} içindeki payı`}>
              İldeki payı
            </th>
            {sort.th('delivery', 'num')}
          </tr>
        </thead>
        <tbody>
          {sort.apply(city.districts).map((d) => (
            <tr key={d.key}>
              <td>{d.name}</td>
              <td className="num">{fmt.n(d.orders)}</td>
              <td className="num">
                <Share rate={d.share} />
              </td>
              <td className="num" title={`${fmt.n(d.delivered)} sipariş teslim edildi`}>
                {daysText(d.deliveryMedianHours)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}
